import { BaseWorkflowActivityAssignmentViewModel } from "../base-workflow-activity-assignment";
import { autoinject, computedFrom } from "aurelia-framework";
import { WorkflowAssignmentsService } from "../../../../services/workflow/workflow-assignments-service";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue } from "../../../../models/lookups/lookup.models";
import { GenesisLookups, LookupAdditionalData } from "../../../../constants/lookups/lookup.constants";
import * as Enumerable from 'linq';
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { UserSecurityInfoModel } from "../../../../models/authentication/token.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { Reflection } from "../../../../utilities/reflection";
import { VettingService } from "../../../../services/vetting/vetting.service";

@autoinject()
export class ActivityAssignment extends BaseWorkflowActivityAssignmentViewModel {

    public configMeta: { view: string, folder: string };
    public acceptItems: LookupValue[];
    public rejectItems: LookupValue[];
    public departments: LookupValue[];
    public user: UserSecurityInfoModel;

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(x => x._model))
    public get completedCount() {
        return this.workflowState.workflowAssignments.filter(x => x.stepNumber < this.assignment.stepNumber).length;
    }

    @computedFrom(Reflection.getPropertyPath<ActivityAssignment>(x => x._model))
    public get perviousStepWasIncomplete(): boolean {
        if (!this.workflowState)
            return false;

        return Enumerable.from(this.workflowState.workflowAssignments)
            .orderBy(x => x.stepNumber).where(x => x.stepNumber == this.getCurrentStep() - 1).any(x => !x.isAccepted);
    }

    constructor(workflowAssignmentsService: WorkflowAssignmentsService, dialogClientService: DialogClientService,
        private userClientService: UserClientService, private lookupClientService: LookupClientService, i18n: I18N, vetClientService: VettingService) {
        super(dialogClientService, i18n, workflowAssignmentsService, vetClientService);
        this.user = this.userClientService.currentUser;
    }

    public getCurrentStep(): number {
        const current = Enumerable.from(this.workflowState.workflowAssignments).where(x => x.isCurrentAssignment).select(x => x.stepNumber);
        return current.any() ? current.min() : null;
    }

    @computedFrom('configMeta')
    get getViewModel() {
        require('views/plugins/workflow-activities/reassign-activity/views/'+ this.configMeta.folder +'/${this.configMeta.view}.ts')
        require('views/plugins/workflow-activities/reassign-activity/views/'+ this.configMeta.folder +'/${this.configMeta.view}.html')
        return `views/plugins/workflow-activities/reassign-activity/views/${this.configMeta.folder}/${this.configMeta.view}`;
    }

    onActivated() {
        const lookupValueQueryDepartments = new LookupValueRequest();
        lookupValueQueryDepartments.lookupValueQuery = GenesisLookups.departments;
        const lookupValueQueryDepartmentsPromise = this.lookupClientService.getLookup(lookupValueQueryDepartments)

        lookupValueQueryDepartmentsPromise.then(a => {
            this.departments = a.allValuesIncludingInactive;
            const lookupValueQuery = new LookupValueRequest();
            lookupValueQuery.lookupValueQuery = GenesisLookups.workflowReasons;
            let scope = null;

            const dep = Enumerable.from(this.departments).firstOrDefault(a => a.id == this.user.departmentId);
            if (dep) {
                const currentScope = dep.additionalData[LookupAdditionalData.scope];
                if (currentScope)
                    scope = currentScope.toLowerCase();
            }
            this.lookupClientService.getLookup(lookupValueQuery).then(a => {
                this.acceptItems = Enumerable.from(a.allValuesIncludingInactive)
                    .where(x => x.additionalData[LookupAdditionalData.isForAccept] == true)
                    .where(x => !scope || !x.additionalData[LookupAdditionalData.visibleForScopes] || Enumerable.from(x.additionalData[LookupAdditionalData.visibleForScopes].split(",")).any(a => a == scope))
                    .where(x => !x.additionalData["entityTypeId"] || x.additionalData["entityTypeId"] == this.entityTypeId)
                    .toArray();
                this.rejectItems = Enumerable.from(a.allValuesIncludingInactive)
                    .where(x => x.additionalData[LookupAdditionalData.isForAccept] == false)
                    .where(x => !scope || !x.additionalData[LookupAdditionalData.visibleForScopes] || Enumerable.from(x.additionalData[LookupAdditionalData.visibleForScopes].split(",")).any(a => a == scope))
                    .where(x => !x.additionalData["entityTypeId"] || x.additionalData["entityTypeId"] == this.entityTypeId)
                    .toArray();
            },  () => { });
        },  () => { });
        this.configMeta = JSON.parse(this.assignment.configMeta);
    }

    submit() {
        return this.accept(null);
    }

    accept(reasonId: number) {
        return new Promise<void>((resolve, reject) => {
            this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                'reassign-activity',
                'accept',
                this.assignment.workflowAssignmentId,
                reasonId,
                this.assignment.remarks,
                this.assignment.meta).then(response => {
                    this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
                    resolve();
                }).catch(reject);
        });
    }

    incomplete(reasonId: number) {
        return new Promise<void>((resolve, reject) => {
            this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                'reassign-activity',
                'incomplete',
                this.assignment.workflowAssignmentId,
                reasonId,
                this.assignment.remarks,
                this.assignment.meta).then(response => {
                    this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
                    resolve();
                }).catch(reject);
        });
    }
}
